<template>
    <div :class="['variation-bulk-actions', { dark: isDark }]">
        <div class="selection-copy" aria-live="polite">
            <strong>{{ selectedItems.length }}</strong>
            {{ selectedItems.length === 1 ? "variation selected" : "variations selected" }}
        </div>
        <div class="tool-buttons">
            <Button noMargin borderOnly type="button" :disabled="!selectedItems.length" @click="openMove">
                <i class="material-icons" aria-hidden="true">drive_file_move</i>
                Move variations
            </Button>
            <Button noMargin borderOnly type="button" :disabled="!selectedItems.length" @click="openStatus">
                <i class="material-icons" aria-hidden="true">published_with_changes</i>
                Update product status
            </Button>
            <button v-if="selectedItems.length" type="button" class="clear-selection" @click="$emit('clear')">
                Clear
            </button>
        </div>

        <CustomModal :show="showMove" title="Move selected variations" @close="closeMove">
            <form class="operation-form" @submit.prevent="submitMove">
                <p class="operation-summary">
                    Move {{ selectedItems.length }} selected variation{{ selectedItems.length === 1 ? "" : "s" }} without
                    changing their SKU, inventory, or history.
                </p>

                <div class="mode-switch" role="radiogroup" aria-label="Choose destination type">
                    <label :class="{ active: moveMode === 'existing' }">
                        <input v-model="moveMode" type="radio" value="existing" />
                        Existing master
                    </label>
                    <label v-if="canCreate" :class="{ active: moveMode === 'new' }">
                        <input v-model="moveMode" type="radio" value="new" />
                        New master
                    </label>
                </div>

                <div v-if="moveMode === 'existing'" class="field-group">
                    <label for="variation-target-master-search">Find a master product</label>
                    <input
                        id="variation-target-master-search"
                        v-model.trim="masterSearch"
                        class="form-control"
                        type="search"
                        autocomplete="off"
                        placeholder="Search by name, item code, or catalog SKU"
                        :disabled="processing"
                        @input="queueMasterSearch"
                    />
                    <small>
                        Search loads matching masters directly, including products outside the initial catalog results.
                    </small>
                    <label for="variation-target-master">Master product</label>
                    <select
                        id="variation-target-master"
                        v-model="targetMasterId"
                        class="form-control"
                        :disabled="loadingMasters || processing"
                    >
                        <option value="">Choose a master product</option>
                        <option v-for="master in eligibleMasters" :key="master.id" :value="master.id">
                            {{ master.title }}{{ master.item_code ? ` · ${master.item_code}` : "" }}
                        </option>
                    </select>
                    <small v-if="loadingMasters">Loading master products…</small>
                    <small v-else-if="!eligibleMasters.length">
                        {{ masterSearch ? "No matching eligible master products were found." : "No eligible master products were found." }}
                    </small>
                    <small v-else>{{ eligibleMasters.length }} matching master{{ eligibleMasters.length === 1 ? "" : "s" }} loaded.</small>
                </div>

                <template v-else>
                    <div class="field-group">
                        <label for="variation-new-master-name">New master name</label>
                        <input
                            id="variation-new-master-name"
                            v-model.trim="newMasterName"
                            class="form-control"
                            type="text"
                            maxlength="140"
                            autocomplete="off"
                            :disabled="processing"
                        />
                        <small>The new master inherits catalog metadata from the first selected variation.</small>
                    </div>
                    <div class="field-group">
                        <label for="variation-new-master-category">Category</label>
                        <select
                            id="variation-new-master-category"
                            v-model="newMasterCategory"
                            class="form-control"
                            :disabled="processing"
                        >
                            <option value="">Use the selected variation category</option>
                            <option v-for="group in itemGroups" :key="group.id || group.name" :value="group.name">
                                {{ group.name }}
                            </option>
                        </select>
                    </div>
                </template>

                <div v-if="operationError" class="operation-error" role="alert">{{ operationError }}</div>
                <div class="modal-actions">
                    <Button noMargin borderOnly type="button" :disabled="processing" @click="closeMove">Cancel</Button>
                    <Button noMargin type="submit" :disabled="!canSubmitMove || processing">
                        {{ processing ? "Moving…" : "Move variations" }}
                    </Button>
                </div>
            </form>
        </CustomModal>

        <CustomModal :show="showStatus" title="Update product status" @close="closeStatus">
            <form class="operation-form" @submit.prevent="submitStatus">
                <p class="operation-summary">
                    The variation records and their sales history remain available. Archived items are disabled, not deleted.
                </p>
                <div class="field-group">
                    <label for="variation-lifecycle-status">New status</label>
                    <select
                        id="variation-lifecycle-status"
                        v-model="nextStatus"
                        class="form-control"
                        :disabled="processing"
                    >
                        <option value="">Choose a status</option>
                        <option v-for="status in lifecycleStatuses" :key="status" :value="status">{{ status }}</option>
                    </select>
                    <small>Only Active variations are enabled for normal catalog selection.</small>
                </div>
                <div v-if="operationError" class="operation-error" role="alert">{{ operationError }}</div>
                <div class="modal-actions">
                    <Button noMargin borderOnly type="button" :disabled="processing" @click="closeStatus">Cancel</Button>
                    <Button noMargin type="submit" :disabled="!nextStatus || processing">
                        {{ processing ? "Updating…" : "Update status" }}
                    </Button>
                </div>
            </form>
        </CustomModal>
    </div>
</template>

<script>
import Button from "../UI/Button.vue"
import CustomModal from "../layout/CustomModal.vue"
import { http } from "@/utils/http"

const LIFECYCLE_STATUSES = ["Active", "Coming Soon", "Inactive", "Discontinued", "Archived"]

export default {
    name: "VariationBulkActions",
    components: { Button, CustomModal },
    props: {
        selectedItems: { type: Array, default: () => [] },
        itemGroups: { type: Array, default: () => [] },
        canCreate: { type: Boolean, default: false },
    },
    data() {
        return {
            lifecycleStatuses: LIFECYCLE_STATUSES,
            showMove: false,
            showStatus: false,
            moveMode: "existing",
            targetMasterId: "",
            newMasterName: "",
            newMasterCategory: "",
            masters: [],
            masterSearch: "",
            masterSearchTimer: null,
            masterSearchRequestId: 0,
            loadingMasters: false,
            processing: false,
            nextStatus: "",
            operationError: "",
        }
    },
    computed: {
        isDark() {
            const misc = this.$store?.state?.misc
            return Boolean(misc && misc.theme === misc.THEMES?.DARK)
        },
        eligibleMasters() {
            const selectedIds = new Set(this.selectedItems.map((item) => item.id))
            return this.masters.filter(
                (master) =>
                    !selectedIds.has(master.id) &&
                    master.product_status !== "Archived" &&
                    !this.selectedItems.every((item) => item.parent === master.id)
            )
        },
        canSubmitMove() {
            return this.selectedItems.length > 0 &&
                (this.moveMode === "existing" ? Boolean(this.targetMasterId) : this.canCreate && Boolean(this.newMasterName))
        },
    },
    beforeDestroy() {
        if (this.masterSearchTimer) window.clearTimeout(this.masterSearchTimer)
        this.masterSearchRequestId += 1
    },
    methods: {
        itemReferences(includeParent = false) {
            return this.selectedItems.map((item) => ({
                id: item.id,
                expected_updated_at: item.updated_at || item.updatedAt || null,
                ...(includeParent ? { expected_parent_id: item.parent } : {}),
            }))
        },
        async openMove() {
            if (!this.selectedItems.length) return
            this.showMove = true
            this.operationError = ""
            this.moveMode = "existing"
            this.targetMasterId = ""
            this.newMasterName = ""
            this.newMasterCategory = ""
            this.masterSearch = ""
            this.masters = []
            await this.fetchMasters()
        },
        closeMove() {
            if (this.processing) return
            this.showMove = false
            this.operationError = ""
            if (this.masterSearchTimer) window.clearTimeout(this.masterSearchTimer)
            this.masterSearchTimer = null
            this.masterSearchRequestId += 1
            this.loadingMasters = false
        },
        openStatus() {
            if (!this.selectedItems.length) return
            this.showStatus = true
            this.nextStatus = ""
            this.operationError = ""
        },
        closeStatus() {
            if (this.processing) return
            this.showStatus = false
            this.operationError = ""
        },
        queueMasterSearch() {
            this.targetMasterId = ""
            this.masterSearchRequestId += 1
            this.loadingMasters = false
            if (this.masterSearchTimer) window.clearTimeout(this.masterSearchTimer)
            this.masterSearchTimer = window.setTimeout(() => {
                this.masterSearchTimer = null
                this.fetchMasters()
            }, 250)
        },
        async fetchMasters() {
            const requestId = this.masterSearchRequestId + 1
            this.masterSearchRequestId = requestId
            this.loadingMasters = true
            this.operationError = ""
            try {
                const response = await http.get("/items/masters/options", {
                    params: {
                        search: this.masterSearch,
                        limit: 50,
                    },
                })
                if (requestId !== this.masterSearchRequestId) return
                this.masters = response.data.result || []
            } catch (err) {
                if (requestId !== this.masterSearchRequestId) return
                this.operationError = err?.response?.data?.message || "Could not load master products."
            } finally {
                if (requestId === this.masterSearchRequestId) this.loadingMasters = false
            }
        },
        async submitMove() {
            if (!this.canSubmitMove || this.processing) return
            this.processing = true
            this.operationError = ""
            try {
                const commonPayload = {
                    variations: this.itemReferences(true),
                }
                const response = this.moveMode === "existing"
                    ? await http.post("/items/variations/move", {
                        ...commonPayload,
                        target_master_id: this.targetMasterId,
                    })
                    : await http.post("/items/variations/move-to-new-master", {
                        ...commonPayload,
                        master: { name: this.newMasterName, item_group: this.newMasterCategory || null },
                    })
                this.showMove = false
                this.$toast.success(response.data.message || "Variations moved successfully")
                this.$emit("completed", response.data.result)
            } catch (err) {
                this.operationError = err?.response?.data?.message || "Could not move the selected variations."
            } finally {
                this.processing = false
            }
        },
        async submitStatus() {
            if (!this.nextStatus || this.processing) return
            this.processing = true
            this.operationError = ""
            try {
                const response = await http.patch("/items/lifecycle-status", {
                    status: this.nextStatus,
                    items: this.itemReferences(false),
                })
                this.showStatus = false
                this.$toast.success(response.data.message || "Product status updated")
                this.$emit("completed", response.data.result)
            } catch (err) {
                this.operationError = err?.response?.data?.message || "Could not update product status."
            } finally {
                this.processing = false
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";
@import "../../assets/mixins.scss";

.variation-bulk-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
    padding: 10px 12px;
    border: 1px solid $lightBorderColor;
    border-radius: 8px;
    background: $lightCardBg;
}

.selection-copy {
    color: $textColor;
    font-size: 13px;
    white-space: nowrap;
}

.tool-buttons,
.modal-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.tool-buttons :deep(.app-btn) {
    min-width: 0;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 10px;
    box-shadow: none;
}

.material-icons {
    font-size: 17px;
}

.clear-selection {
    min-height: 34px;
    padding: 0 8px;
    border: 0;
    background: transparent;
    color: $primaryColor;
    cursor: pointer;

    &:focus-visible { @include focus-ring; }
}

.operation-form {
    display: grid;
    gap: 16px;
}

.operation-summary {
    margin: 0;
    color: rgba($textColor, 0.75);
    font-size: 13px;
    line-height: 1.5;
}

.mode-switch {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;

    label {
        display: flex;
        align-items: center;
        gap: 8px;
        min-height: 42px;
        margin: 0;
        padding: 9px 10px;
        border: 1px solid $lightBorderColor;
        border-radius: 6px;
        color: $textColor;
        cursor: pointer;

        &.active {
            border-color: $primaryColor;
            background: rgba($primaryColor, 0.06);
        }
    }
}

.field-group {
    display: grid;
    gap: 6px;

    label {
        margin: 0;
        color: $textColor;
        font-size: 13px;
        font-weight: 600;
    }

    small {
        color: rgba($textColor, 0.68);
        line-height: 1.4;
    }
}

.form-control {
    min-height: 40px;
    border-color: $lightBorderColor;

    &:focus {
        border-color: $primaryColor;
        box-shadow: 0 0 0 2px rgba($primaryColor, 0.12);
    }
}

.operation-error {
    padding: 9px 10px;
    border-left: 3px solid $dangerColor;
    background: rgba($dangerColor, 0.08);
    color: darken($dangerColor, 10%);
    font-size: 13px;
}

.modal-actions {
    justify-content: flex-end;
    padding-top: 4px;
}

.variation-bulk-actions.dark {
    border-color: $darkBorderColor;
    background: $darkCardBg;

    .selection-copy,
    .operation-summary,
    .field-group label,
    .mode-switch label {
        color: $darkTextColor;
    }

    .field-group small { color: rgba($darkTextColor, 0.7); }

    .mode-switch label {
        border-color: $darkBorderColor;

        &.active {
            border-color: $secondaryColorDark;
            background: rgba($secondaryColorDark, 0.1);
        }
    }

    .form-control {
        border-color: $darkBorderColor;
        background: $darkCardBg;
        color: $darkTextColor;
    }
}

@media (max-width: 720px) {
    .variation-bulk-actions {
        align-items: stretch;
        flex-direction: column;
    }

    .tool-buttons {
        display: grid;
        grid-template-columns: 1fr;
    }

    .tool-buttons :deep(.app-btn),
    .clear-selection {
        width: 100%;
        justify-content: center;
    }

    .mode-switch { grid-template-columns: 1fr; }
    .modal-actions { flex-direction: column-reverse; }
    .modal-actions :deep(.app-btn) { width: 100%; }
}
</style>
